import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $cardsListStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, FormatUtils } from '@shared/utils';
import { AccountType, UserType } from '@shared/api';
import type { AddNewUserFormData } from './addNewUserModal';
import { ErrorMessage, Multiselect, Select } from '@shared/widgets';

export const SelectUserAccountsAndCards = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsListStore = useUnit($cardsListStore.store);

  const { control, watch } = useFormContext<AddNewUserFormData>();
  const [userType, account] = watch(['userType', 'account']);

  useEffect(() => {
    if (!account) return;
    $cardsListStore.events.getUserCards({ AccountId: account, Count: 100 });
    return () => $cardsListStore.events.clear();
  }, [account]);

  const { t } = useTranslation();

  const accountOptions = useMemo(() => {
    if (!profileFeatureStore.data?.accountFlows) return [];
    return AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, accountsStore.data)
      .filter((account) => account.mainAccId == null)
      .map((account) => ({
        label: account.type === AccountType.Payment ? account.accountNumber : FormatUtils.toSliceNumber(account.accountNumber),
        value: account.id,
      }));
  }, [accountsStore.loading, profileFeatureStore.data?.accountFlows]);
  const cardOptions = useMemo(
    () => cardsListStore.data.map((card) => ({ label: FormatUtils.toSliceNumber(card.cardNumber), value: card.cardId })),
    [cardsListStore.data],
  );
  return (
    <>
      {userType === UserType.User && (
        <div className='flex flex-col gap-4'>
          <Controller
            name='account'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div>
                <Select
                  isLoading={accountsStore.loading}
                  nullValue={{ isEnable: false, text: t('placeholders.chooseAccount') }}
                  items={accountOptions}
                  required
                  value={value}
                  onChange={onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='cards'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div>
                <Multiselect
                  isLoading={cardsListStore.loading}
                  nullValue={{ isEnable: false, text: t('placeholders.chooseCards') }}
                  items={cardOptions}
                  required
                  value={value}
                  onChange={onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        </div>
      )}
      {userType === UserType.Manager && (
        <Controller
          name='accounts'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <Multiselect
                isLoading={accountsStore.loading}
                nullValue={{ isEnable: false, text: t('placeholders.chooseAccounts') }}
                items={accountOptions}
                required
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      )}
    </>
  );
};
